import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';

/**
 * Checks if a control (or any of its children, if it's a FormGroup) has a filled value.
 * Returns true if the control has a non-null and non-empty string value.
 * For FormGroups, it returns true if any direct or nested child control is filled.
 */
export function isControlFilled(control: AbstractControl): boolean {
  if (!control) {
    return false;
  }

  if (control instanceof FormGroup) {
    for (const key of Object.keys(control.controls)) {
      const innerControl = control.get(key);
      if (innerControl) {
        if (isControlFilled(innerControl)) {
          return true;
        }
      }
    }
    return false;
  } else {
    const value = control.value;
    return value !== null && value !== '';
  }
}

/**
 * Removes one or more specific errors from an AbstractControl.
 * If the AbstractControl is a FormGroup, errors will be removed from all its child controls recursively.
 * If the control has no more errors after removal, its validation status is cleared.
 *
 * @param control The AbstractControl from which errors will be removed.
 * @param errorNames The name of the error (string) or an array of error names (string[]) to remove.
 */
export function removeControlErrors(
  control: AbstractControl,
  errorNames: string | string[]
): void {
  if (!control) {
    return;
  }

  const errorsToRemove = Array.isArray(errorNames) ? errorNames : [errorNames];

  if (control.errors) {
    let currentErrors: ValidationErrors | null = control.errors;

    for (const errorName of errorsToRemove) {
      if (currentErrors && currentErrors[errorName] !== undefined) {
        const { [errorName]: removedError, ...otherErrors } = currentErrors;
        currentErrors =
          Object.keys(otherErrors).length > 0 ? otherErrors : null;
      }
    }
    control.setErrors(currentErrors);
  }

  if (control instanceof FormGroup) {
    Object.keys(control.controls).forEach((key) => {
      const innerControl = control.get(key);
      if (innerControl) {
        removeControlErrors(innerControl, errorsToRemove);
      }
    });
  }
}

/**
 * Adds one or more errors to an AbstractControl.
 * New errors are merged with any existing errors on the control.
 *
 * @param control The AbstractControl to which errors will be added.
 * @param errorsToAdd An error object ({ errorName: value }) or an array of error objects.
 * E.g., { 'required': true }, { 'minlength': { requiredLength: 5, actualLength: 3 } }
 * or [{ 'invalidFormat': true }, { 'customError': 'Invalid value' }]
 */
export function addControlErrors(
  control: AbstractControl,
  errorsToAdd: ValidationErrors | ValidationErrors[]
): void {
  if (!control) {
    return;
  }

  const errorsArray = Array.isArray(errorsToAdd) ? errorsToAdd : [errorsToAdd];

  let currentErrors: ValidationErrors | null = control.errors;

  for (const newErrorObj of errorsArray) {
    currentErrors = { ...(currentErrors || {}), ...newErrorObj };
  }

  control.setErrors(currentErrors);
}
